<script setup lang="ts">
import type { ToastHtmlElement } from "@/types"
import { useMutation, useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, ref, toRef, watchEffect } from "vue"
import { z } from "zod"
import { updateMessage } from "@/api"
import { BaseModal, Button, Card, Icon, Input, Textarea, Toast } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { GuestbookMessage } from "@/models/message"
import { MessageEditModalKey } from "@/symbols"

const props = defineProps<{
  eventVaultId?: number
  message?: GuestbookMessage
}>()

const { t } = useTranslation()
const { toggle } = useModal(MessageEditModalKey)
const queryClient = useQueryClient()

const messageId = toRef(props.message?.id)
const validationErrors = ref()
const successToast = ref<ToastHtmlElement | null>(null)
const errorToast = ref<ToastHtmlElement | null>(null)

const validationSchema = toTypedSchema(
  z.object({
    fromName: z.optional(z.string()),
    fromEmail: z.union([z.literal(""), z.string().email(t("invalid_email"))]),
    message: z
      .string()
      .min(1, t("message_edit_modal.message_field_validation_min"))
      .max(500, t("message_edit_modal.message_field_validation_max", { n: 500 })),
  }),
)

const { setValues, errors, handleSubmit } = useForm({
  validationSchema,
  initialValues: {
    fromName: props.message?.fromName ?? "",
    fromEmail: props.message?.fromEmail ?? "",
    message: props.message?.message ?? "",
  },
})

const { mutate, isPending } = useMutation({
  mutationKey: ["updateMessage", messageId, props.eventVaultId],
  mutationFn: (message: GuestbookMessage) => updateMessage(message, props.eventVaultId),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ["listMessages"] })
    toggle()
    successToast.value?.showToast({ content: t("message_edit_modal.success_message"), title: t("success"), icon: "CircleCheck" })
  },
  onError: (error) => {
    if (error instanceof ValidationException) {
      validationErrors.value = error
    }

    errorToast.value?.showToast()
  },
})

const submit = handleSubmit((values) => {
  const updatedMessage = new GuestbookMessage({
    ...props.message,
    message: values.message,
  })

  mutate(updatedMessage)
})

watchEffect(() => {
  setValues({
    fromName: props.message?.fromName ?? "",
    fromEmail: props.message?.fromEmail ?? "",
    message: props.message?.message ?? "",
  })
})

const hasErrors = computed<boolean>(() => {
  return Object.keys(errors.value).length > 0
})
</script>

<template>
  <BaseModal :name="MessageEditModalKey">
    <Card>
      <template #header>
        <div class="gpx-message-edit-header">
          <h1>{{ t("message_edit_modal.title") }}</h1>
          <Button class="gpx-message-edit-close" variant="flat" @click="toggle">
            <Icon icon="Close" />
            <span class="sr-only">{{ t("message_edit_modal.close") }}</span>
          </Button>
        </div>
      </template>
      <div v-show="isPending" class="gpx-message-edit-fields">
        <div class="gpx-skeleton gpx-message-edit-input_loading" />
        <div class="gpx-skeleton gpx-message-edit-input_loading" />
        <div class="gpx-skeleton gpx-message-edit-textarea_loading" />
        <div class="gpx-message-edit-actions">
          <div class="gpx-skeleton gpx-message-edit-button_loading" />
          <div class="gpx-skeleton gpx-message-edit-button_loading" />
        </div>
      </div>
      <form v-show="!isPending" @submit.prevent="submit">
        <div class="gpx-message-edit-fields">
          <Input name="fromName" :label="t('message_edit_modal.name_label')" disabled />
          <Input name="fromEmail" :label="t('message_edit_modal.email_label')" disabled />
          <Textarea name="message" :label="t('message')" :error="validationErrors?.firstValidationError('Message')" />
          <div class="gpx-message-edit-actions">
            <Button size="lg" variant="flat" @click.prevent="toggle">
              {{ t("cancel") }}
            </Button>
            <Button size="lg" :disabled="hasErrors">
              {{ t("save") }}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  </BaseModal>

  <Toast ref="errorToast" variant="danger" />
  <Toast ref="successToast" variant="success" />
</template>

<style scoped>
.gpx-message-edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.gpx-message-edit-close {
  padding: 0;
}

.gpx-message-edit-header h1 {
  margin: 0;
  padding: 0;
  font-family: Comfortaa, "Open Sans", sans-serif;
  font-weight: bold;
  font-size: 1.375rem;
}

.gpx-message-edit-fields {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.gpx-message-edit-actions {
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 1rem;
}

.gpx-message-edit-input_loading {
  width: 100%;
  height: 3.5rem;
}

.gpx-message-edit-textarea_loading {
  width: 100%;
  height: 10rem;
}

.gpx-message-edit-button_loading {
  width: 10rem;
  height: 3.5rem;
}
</style>
